// Promos de crédito de «Elige el teléfono»: filas del banner morado y las que tocan a un equipo en su detalle.
// Puro, sin React.
import { effectChips, levelLabel, reachesLevel } from './promotionDetail';
import type { CatalogItem, PlanLevel, PromotionEffect, ScopeWidePromotion } from './types';

/** A qué aplica: toda la tienda o N equipos de la pantalla. */
export type CreditPromotionReach = { kind: 'store' } | { kind: 'items'; count: number };

export interface CreditPromotionRow {
    promotionId: string;
    name: string;
    description: string;
    effect: PromotionEffect;
    /** Niveles que alcanza; vacío = cualquiera. */
    targetLevels: PlanLevel[];
    reach: CreditPromotionReach;
}

function byName(a: CreditPromotionRow, b: CreditPromotionRow): number {
    return a.name.localeCompare(b.name, 'es-MX');
}

/** Las de toda la tienda y las de algunos equipos, sin repetir y en orden alfabético. */
export function creditPromotionRows(
    storeWide: readonly ScopeWidePromotion[],
    items: readonly Pick<CatalogItem, 'promotions'>[],
    clientLevel: PlanLevel | null | undefined,
): CreditPromotionRow[] {
    const storeIds = new Set(storeWide.map((p) => p.promotionId));
    const rows: CreditPromotionRow[] = storeWide
        .filter((p) => reachesLevel(p.targetLevels, clientLevel))
        .map((p) => ({
            promotionId: p.promotionId,
            name: p.name,
            description: p.description,
            effect: p.effect,
            targetLevels: p.targetLevels,
            reach: { kind: 'store' },
        }));
    const bySku = new Map<
        string,
        CreditPromotionRow & { reach: { kind: 'items'; count: number } }
    >();
    for (const item of items) {
        for (const p of item.promotions ?? []) {
            if (storeIds.has(p.promotionId) || !reachesLevel(p.targetLevels, clientLevel)) continue;
            const found = bySku.get(p.promotionId);
            if (found) found.reach.count += 1;
            else
                bySku.set(p.promotionId, {
                    promotionId: p.promotionId,
                    name: p.name,
                    description: '',
                    effect: p.effect,
                    targetLevels: p.targetLevels,
                    reach: { kind: 'items', count: 1 },
                });
        }
    }
    return [...rows, ...bySku.values()].sort(byName);
}

/** Las que mejoran el crédito de este equipo: las de toda la tienda y las que lo nombran. */
export function creditPromotionsForItem(
    rows: readonly CreditPromotionRow[],
    item: Pick<CatalogItem, 'promotions'>,
): CreditPromotionRow[] {
    const own = new Set((item.promotions ?? []).map((p) => p.promotionId));
    return rows.filter((r) => r.reach.kind === 'store' || own.has(r.promotionId));
}

/** Bono a la cuenta que da una promo de crédito al activar el crédito. */
export interface CreditBonus {
    promotionId: string;
    name: string;
    amountCents: number;
    targetLevels: PlanLevel[];
    reach: CreditPromotionReach;
}

/** Separa el bono de cada promo (va a «Bonos a tu cuenta») de lo que mejora el crédito. */
export function splitCreditRows(rows: readonly CreditPromotionRow[]): {
    bonuses: CreditBonus[];
    improvements: CreditPromotionRow[];
} {
    const bonuses: CreditBonus[] = [];
    const improvements: CreditPromotionRow[] = [];
    for (const row of rows) {
        const { pcfBonusCents, ...rest } = row.effect;
        const hasBonus = typeof pcfBonusCents === 'number' && pcfBonusCents > 0;
        if (hasBonus) {
            bonuses.push({
                promotionId: row.promotionId,
                name: row.name,
                amountCents: pcfBonusCents,
                targetLevels: row.targetLevels,
                reach: row.reach,
            });
        }
        if (!hasBonus) improvements.push(row);
        else if (effectChips(rest).length > 0) improvements.push({ ...row, effect: rest });
    }
    return { bonuses, improvements };
}

/** En el detalle: «toda la tienda», «este equipo» o «N equipos, incluido este». */
export function detailReachLabel(reach: CreditPromotionReach): string {
    if (reach.kind === 'store') return 'toda la tienda';
    return reach.count === 1 ? 'este equipo' : `${String(reach.count)} equipos, incluido este`;
}

/** «Solo nivel Oro» o «Solo nivel Plata u Oro»; `null` si alcanza a cualquier nivel. */
export function levelOnlyLabel(levels: readonly PlanLevel[]): string | null {
    if (levels.length === 0) return null;
    const names = levels.map(levelLabel);
    const last = names.at(-1) ?? '';
    const joined =
        names.length === 1
            ? last
            : `${names.slice(0, -1).join(', ')} ${/^[oO]/.test(last) ? 'u' : 'o'} ${last}`;
    return `Solo nivel ${joined}`;
}

/** «Toda la tienda», «1 equipo» o «N equipos». */
export function reachLabel(reach: CreditPromotionReach): string {
    if (reach.kind === 'store') return 'Toda la tienda';
    return reach.count === 1 ? '1 equipo' : `${String(reach.count)} equipos`;
}
